import { TextInput } from 'components/inputs';
import styled from '@emotion/styled';
import { outlinedInputClasses } from '@mui/material';
import { useState, Fragment, useMemo, useRef } from 'react';

type OTPProps = {
  numbers?: number;
  dividers?: number;
  onComplete?: (code: string) => void;
  onIncomplete?: () => void;
  initialValue?: string;
};

/**
 * One time password component
 * @param numbers - amount of inputs/numbers
 * @param dividers - amount of dash dividers, will be evenly spaced between inputs
 * @param onComplete - will be called with full string when all fields are filled out
 */
export const OTP = ({
  numbers = 6,
  dividers = 1,
  onComplete,
  onIncomplete,
  initialValue,
}: OTPProps) => {
  const [values, setValues] = useState<string[]>(() => {
    if (initialValue && initialValue.length <= numbers) {
      return initialValue.split('').concat(new Array(numbers - initialValue.length).fill(''));
    }
    return new Array(numbers).fill('');
  });
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const moveCursor = (inputIndex: number) => {
    const targetInput = inputRefs.current[inputIndex];
    if (targetInput) {
      targetInput.focus();
    }
  };

  const handleChange =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const inputValue = event.target.value.toUpperCase();
      // Only allow single alphanumeric character (0-9, A-Z)
      if (/^[A-Z0-9]{0,1}$/.test(inputValue)) {
        const newValues = [...values];
        newValues[index] = inputValue;

        // Check completion state before updating values
        const wasComplete = values.every(value => value !== '');
        const isComplete = newValues.every(value => value !== '');

        setValues(newValues);

        // Call callbacks after state update
        if (isComplete && onComplete) {
          onComplete(newValues.join(''));
        } else if (wasComplete && !isComplete && onIncomplete) {
          onIncomplete();
        }

        // Auto-focus next input if a character was entered
        if (inputValue && index < numbers - 1) {
          moveCursor(index + 1);
        }
      }
    };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text').toUpperCase();

    // Only process if the pasted data contains valid characters
    if (/^[A-Z0-9]+$/.test(pastedData)) {
      const newValues = [...values];
      const charsToUse = pastedData.slice(0, numbers);

      // Fill the values array with the pasted characters
      for (let i = 0; i < numbers; i++) {
        newValues[i] = charsToUse[i] || '';
      }

      // Check completion state before updating values
      const wasComplete = values.every(value => value !== '');
      const isComplete = newValues.every(value => value !== '');

      setValues(newValues);

      // Call callbacks after state update
      if (isComplete && onComplete) {
        onComplete(newValues.join(''));
      } else if (wasComplete && !isComplete && onIncomplete) {
        onIncomplete();
      }

      // Focus the next empty field or the last field if all are filled
      const nextEmptyIndex = newValues.findIndex(value => value === '');
      const targetIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : numbers - 1;
      moveCursor(targetIndex);
    }
  };

  const deleteValue = (index: number) => {
    const newValues = [...values];
    newValues[index] = '';

    // Check completion state before updating values
    const wasComplete = values.every(value => value !== '');
    const isComplete = newValues.every(value => value !== '');

    setValues(newValues);

    // Call callbacks after state update
    if (wasComplete && !isComplete && onIncomplete) {
      onIncomplete();
    }
  };

  const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (values[index]) {
        // If current field has a value, clear it and prevent default
        deleteValue(index);
        return;
      }
      if (index > 0) {
        // If current field is empty, move to previous and clear it
        deleteValue(index - 1);
        moveCursor(index - 1);
      }
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      if (values[index]) {
        // If current field has a value, clear it and prevent default
        deleteValue(index);
        return;
      }
      if (index < numbers - 1) {
        // If current field is empty, move to next and clear it
        deleteValue(index + 1);
        moveCursor(index + 1);
      }
    }

    // Handle arrow key navigation
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      moveCursor(index - 1);
    }
    if (event.key === 'ArrowRight' && index < numbers - 1) {
      event.preventDefault();
      moveCursor(index + 1);
    }
  };

  const handleFocus = (index: number) => () => {
    setFocusedIndex(index);
  };

  const handleBlur = () => {
    setFocusedIndex(null);
  };

  const dividerPositions = useMemo(() => {
    if (dividers <= 0) return [];
    const positions = [];
    const step = numbers / (dividers + 1);

    for (let i = 1; i <= dividers; i++) {
      const position = Math.floor(step * i);
      if (position < numbers) {
        positions.push(position);
      }
    }
    return positions;
  }, [dividers, numbers]);

  return (
    <Container>
      {Array.from({ length: numbers }, (_, index) => (
        <Fragment key={index}>
          <TextInput
            variant="outlined"
            value={values[index]}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste}
            onFocus={handleFocus(index)}
            onBlur={handleBlur}
            placeholder={focusedIndex === index || values[index] ? '' : '0'}
            ref={el => {
              inputRefs.current[index] = el;
            }}
            InputProps={{
              inputProps: {
                maxLength: 1,
                inputMode: 'text',
                pattern: '[A-Z0-9]*',
              },
            }}
            autoFocus={index === 0}
          />
          {dividerPositions.includes(index + 1) && <Divider>-</Divider>}
        </Fragment>
      ))}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;

  &&& {
    .${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline} {
      border-color: ${({ theme }) => theme.palette.border.accentPrimary};
      box-shadow: none;
    }
    .${outlinedInputClasses.root} {
      max-height: 60px;
      max-width: 60px;
    }
  }
  &&& .MuiAutocomplete-input,
  &&& input {
    height: 40px;
    width: 29px;
    padding: 10px 8px;
    text-align: center;
    font-size: ${({ theme }) => theme.typography.headlineLg.fontSize};
    font-weight: ${({ theme }) => theme.typography.weight.semiBold};
    &::placeholder,
    &::-webkit-input-placeholder,
    &::-moz-placeholder,
    &:-ms-input-placeholder,
    &::-ms-input-placeholder {
      font-size: ${({ theme }) => theme.typography.headlineLg.fontSize};
      font-weight: ${({ theme }) => theme.typography.weight.semiBold};
      color: ${({ theme }) => theme.palette.text.placeholderSubtle};
    }
  }
`;

const Divider = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
`;
